import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ResponsiveBar} from '@nivo/bar';
import {Globals as ReactSpringGlobals} from '@react-spring/shared';
import {useSelector} from 'react-redux';

import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import request from 'utils/request';
import {getDefaultRange, validateRange, TRANSLATION_USAGE_TIMEZONE} from 'utils/translationUsage';
import './style.scss';

export {getDefaultRange, validateRange} from 'utils/translationUsage';

const TIMEZONE = TRANSLATION_USAGE_TIMEZONE;
const basicUserSelector = makeSelectBasicUser();

ReactSpringGlobals.assign({willAdvance: () => {}});

const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const formatUsd = (micros) => {
  const dollars = asNumber(micros) / 1000000;
  return dollars > 0 && dollars < 0.01
    ? `$${dollars.toFixed(4)}`
    : `$${dollars.toFixed(2)}`;
};

export const formatAverageTranslationCost = (spentMicros, succeeded) =>
  asNumber(succeeded) > 0
    ? formatUsd(asNumber(spentMicros) / asNumber(succeeded))
    : '—';

export const formatManualEditRate = (rate) =>
  typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 && rate <= 1
    ? `${(rate * 100).toFixed(1)}%`
    : '—';

const providerLabel = (provider) => {
  if (provider === 'deepseek') return 'DeepSeek';
  if (provider === 'gemini') return 'Gemini';
  if (provider === 'openai_luna') return 'OpenAI Luna';
  if (provider === 'google_nmt') return 'Google NMT';
  return provider || 'Not configured';
};

const treatmentLabel = (treatment) =>
  String(treatment.label || treatment.treatmentId || '').replace(/^Model /, 'Treatment ');

const configurationLabel = (treatment) => {
  const configuration = treatment.configuration || {};
  if (configuration.modelFamilyLabel) return configuration.modelFamilyLabel;
  if (!configuration.configured) return 'Not configured';
  return [providerLabel(configuration.provider), configuration.model]
    .filter(Boolean)
    .join(' · ');
};

export const buildChartData = (treatments = []) =>
  treatments.map((treatment) => ({
    treatment: treatmentLabel(treatment),
    'Adoption rate':
      typeof treatment.adoptionRate === 'number'
        ? treatment.adoptionRate * 100
        : 0,
    'Manual-edit rate':
      typeof treatment.quality?.manualEditRate === 'number'
        ? treatment.quality.manualEditRate * 100
        : 0,
  }));

const StatusValue = ({label, value}) => (
  <div className="model-usage__status-value">
    <span>{label}</span>
    <strong>{asNumber(value)}</strong>
  </div>
);

const TreatmentCard = ({treatment, index}) => {
  const generation = treatment.generation || {};
  return (
    <section
      className={`model-usage__card model-usage__card--treatment-${index % 6}`}
      aria-label={`${configurationLabel(treatment)} — ${treatmentLabel(treatment)} usage`}
    >
      <div className="model-usage__card-header">
        <h2>{configurationLabel(treatment)}</h2>
        <p>{treatmentLabel(treatment)}</p>
      </div>
      <div className="model-usage__total">
        <span>Assigned comparisons</span>
        <strong>{asNumber(treatment.assignedComparisons)}</strong>
      </div>
      <div className="model-usage__status-grid">
        <StatusValue label="Complete decisions" value={treatment.completeDecisions} />
        <StatusValue label="Awaiting decision" value={treatment.awaitingDecision} />
        <StatusValue label="Adoptions" value={treatment.adoptions} />
        <StatusValue label="Succeeded" value={generation.succeeded} />
        <StatusValue label="Failed" value={generation.failed} />
        <StatusValue label="Processing" value={generation.processing} />
        <StatusValue label="Provider calls" value={treatment.providerCalls?.total} />
        <StatusValue label="Eligible units" value={treatment.eligibility?.eligible} />
        <StatusValue label="Excluded units" value={treatment.eligibility?.excluded} />
      </div>
      <div className="model-usage__rate-grid">
        <div className="model-usage__cost">
          <span>Adoption rate</span>
          <strong>{formatManualEditRate(treatment.adoptionRate)}</strong>
          <small>Selected / complete comparisons</small>
        </div>
        <div className="model-usage__cost">
          <span>Manual-edit rate</span>
          <strong>{formatManualEditRate(treatment.quality?.manualEditRate)}</strong>
          <small>
            {asNumber(treatment.quality?.editedSegments)} edited /{' '}
            {asNumber(treatment.quality?.measuredSegments)} measured text units
            {' · '}{asNumber(treatment.quality?.awaitingPublication)} awaiting publication
          </small>
        </div>
        <div className="model-usage__cost">
          <span>Eligibility coverage</span>
          <strong>{formatManualEditRate(treatment.eligibility?.coverage)}</strong>
          <small>
            {asNumber(treatment.eligibility?.eligible)} eligible /{' '}
            {asNumber(treatment.eligibility?.considered)} considered units
          </small>
        </div>
        <div className="model-usage__cost">
          <span>Ready within five minutes</span>
          <strong>{formatManualEditRate(treatment.reliability?.rate)}</strong>
          <small>
            {asNumber(treatment.reliability?.withinFiveMinutes)} within target /{' '}
            {asNumber(treatment.reliability?.observed)} observed comparisons
          </small>
        </div>
      </div>
      <div className="model-usage__cost-grid">
        <div className="model-usage__cost">
          <span>Settled cost</span>
          <strong>{formatUsd(treatment.cost?.spentMicros)}</strong>
        </div>
        <div className="model-usage__cost">
          <span>Average cost per translation</span>
          <strong>
            {formatAverageTranslationCost(
              treatment.cost?.spentMicros,
              generation.succeeded
            )}
          </strong>
          <small>Settled cost / successful translations</small>
        </div>
      </div>
    </section>
  );
};

const ComparisonChart = ({data}) => (
  <div className="model-usage__chart-scroll">
    <div className="model-usage__chart">
      <ResponsiveBar
        data={data}
        keys={['Adoption rate', 'Manual-edit rate']}
        indexBy="treatment"
        groupMode="grouped"
        margin={{top: 28, right: 20, bottom: 58, left: 52}}
        padding={0.3}
        colors={['#79bffc', '#deb851']}
        maxValue={100}
        axisBottom={{
          tickSize: 5,
          tickPadding: 6,
          legend: 'Configured treatment',
          legendPosition: 'middle',
          legendOffset: 42,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 6,
          legend: 'Rate (%)',
          legendPosition: 'middle',
          legendOffset: -42,
        }}
        enableLabel={false}
        tooltip={({id, value, color, indexValue}) => (
          <div className="model-usage__tooltip">
            <span style={{backgroundColor: color}} />
            <strong>{indexValue}</strong> · {id}: {asNumber(value).toFixed(1)}%
          </div>
        )}
        legends={[{
          dataFrom: 'keys',
          anchor: 'top-left',
          direction: 'row',
          translateY: -24,
          itemsSpacing: 18,
          itemWidth: 130,
          itemHeight: 20,
          symbolSize: 11,
        }]}
        animate={false}
      />
    </div>
  </div>
);

const PairTable = ({pairs}) => (
  <div className="model-usage__pair-table-wrap">
    <table className="model-usage__pair-table">
      <thead>
        <tr>
          <th>Pair</th>
          <th>Complete decisions</th>
          <th>Adoption split</th>
        </tr>
      </thead>
      <tbody>
        {pairs.map((pair) => (
          <tr key={pair.pairKey}>
            <td>{pair.treatments.map((id) => id.replace(/^MODEL_/, '')).join(' vs ')}</td>
            <td>{asNumber(pair.completeDecisions)}</td>
            <td>
              {pair.treatments.map((id) =>
                `${id.replace(/^MODEL_/, '')}: ${formatManualEditRate(pair.adoptionRates?.[id])}`
              ).join(' · ')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AutomaticSummary = ({automatic}) => (
  <section className="model-usage__card model-usage__card--treatment-0">
    <div className="model-usage__card-header">
      <h2>Automatic translation</h2>
      <p>{configurationLabel(automatic)}</p>
    </div>
    <div className="model-usage__total">
      <span>Drafts created</span>
      <strong>{asNumber(automatic.draftsCreated)}</strong>
    </div>
    <div className="model-usage__status-grid">
      <StatusValue label="Jobs" value={automatic.generation?.total} />
      <StatusValue label="Succeeded" value={automatic.generation?.succeeded} />
      <StatusValue label="Failed" value={automatic.generation?.failed} />
    </div>
    <div className="model-usage__cost-grid">
      <div className="model-usage__cost">
        <span>Settled cost</span>
        <strong>{formatUsd(automatic.cost?.spentMicros)}</strong>
      </div>
      <div className="model-usage__cost">
        <span>Average cost per translation</span>
        <strong>
          {formatAverageTranslationCost(
            automatic.cost?.spentMicros,
            automatic.generation?.succeeded
          )}
        </strong>
        <small>Settled cost / successful translations</small>
      </div>
    </div>
  </section>
);

const ModelUsage = () => {
  const basicUser = useSelector(basicUserSelector);
  const initialRange = useMemo(() => getDefaultRange(), []);
  const mountedRef = useRef(false);
  const requestVersionRef = useRef(0);
  const lastRequestedRangeRef = useRef(initialRange);
  const [range, setRange] = useState(initialRange);
  const [activeRange, setActiveRange] = useState(initialRange);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const loadMetrics = useCallback(async (nextRange) => {
    if (!basicUser.authToken) return;
    lastRequestedRangeRef.current = {...nextRange};
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams(nextRange).toString();
      const response = await request(
        `translations/admin/model-experiment-usage?${query}`,
        basicUser.authToken
      );
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setMetrics(response?.data || response);
        setActiveRange(nextRange);
      }
    } catch (requestError) {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setError(requestError?.message || 'Model usage could not be loaded.');
      }
    } finally {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [basicUser.authToken]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, []);

  useEffect(() => {
    loadMetrics(initialRange);
  }, [initialRange, loadMetrics]);

  const applyRange = () => {
    const message = validateRange(range);
    setValidationError(message);
    if (!message) loadMetrics(range);
  };

  const treatments = Array.isArray(metrics?.treatments) ? metrics.treatments : [];
  const pairs = Array.isArray(metrics?.pairComparisons) ? metrics.pairComparisons : [];
  const chartData = buildChartData(treatments);
  const warningMicros = asNumber(metrics?.warnings?.unattributedActualMicros) +
    asNumber(metrics?.warnings?.uncertainMaximumMicros);
  const unmeasurableArticles = asNumber(metrics?.warnings?.unmeasurableArticles);

  return (
    <section className="model-usage" aria-labelledby="model-usage-title">
      <div className="model-usage__heading">
        <div>
          <h1 id="model-usage-title">{metrics?.mode === 'AUTOMATIC' ? 'Translation Model Usage' : 'Translation Model Experiment'}</h1>
          {metrics?.mode === 'AUTOMATIC' ? (
            <p>
              Automatic translation usage is grouped by model family across versions.
              Costs include settled provider charges only. Timezone: {TIMEZONE}.
            </p>
          ) : (
            <p>
              Administrators can see the real provider behind each treatment.
              Editors only see Model 1 and Model 2. Adoption rate uses completed
              comparisons; manual-edit rate is weighted by translated text units.
              Eligibility coverage keeps capability exclusions visible. The five-minute
              measure runs from the first queued candidate to both candidates being ready.
              Timezone: {TIMEZONE}.
            </p>
          )}
        </div>
        <div className="model-usage__filters" aria-label="Usage date range">
          <label>
            From
            <input type="date" value={range.from} max={getDefaultRange().to}
              onChange={(event) => setRange({...range, from: event.target.value})} />
          </label>
          <label>
            To
            <input type="date" value={range.to} max={getDefaultRange().to}
              onChange={(event) => setRange({...range, to: event.target.value})} />
          </label>
          <button type="button" className="model-usage__apply" onClick={applyRange}>Apply</button>
          <button type="button" className="model-usage__refresh"
            onClick={() => loadMetrics(activeRange)} disabled={loading}>Refresh</button>
        </div>
      </div>

      {validationError && <p className="model-usage__message model-usage__message--error" role="alert">{validationError}</p>}
      {error && (
        <div className="model-usage__message model-usage__message--error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => loadMetrics(lastRequestedRangeRef.current)}>Retry</button>
        </div>
      )}
      {warningMicros > 0 && (
        <p className="model-usage__message model-usage__message--warning">
          Some ledger amounts could not be attributed safely or still have an
          uncertain maximum. They are excluded from treatment cost totals.
        </p>
      )}
      {unmeasurableArticles > 0 && (
        <p className="model-usage__message model-usage__message--warning">
          {unmeasurableArticles} selected translation article
          {unmeasurableArticles === 1 ? ' has' : 's have'} no comparable
          first-publication segment evidence and is excluded from manual-edit rates.
        </p>
      )}
      {loading && !metrics && <p className="model-usage__message">Loading model experiment…</p>}

      {metrics?.mode === 'AUTOMATIC' && metrics.automatic && (
        <div className="model-usage__cards">
          {(metrics.automaticModels || [metrics.automatic]).map((model) => (
            <AutomaticSummary key={model.modelFamilyKey || model.treatmentId} automatic={model} />
          ))}
        </div>
      )}

      {metrics?.mode === 'EXPERIMENT' && (
        <>
          <div className="model-usage__cards" aria-live="polite">
            {treatments.map((treatment, index) => (
              <TreatmentCard key={treatment.treatmentId} treatment={treatment} index={index} />
            ))}
          </div>
          <section className="model-usage__chart-panel" aria-labelledby="model-usage-chart-title">
            <div className="model-usage__chart-heading">
              <h2 id="model-usage-chart-title">Adoption and editing comparison</h2>
              <span>{activeRange.from} to {activeRange.to}</span>
            </div>
            {!treatments.length ? (
              <p className="model-usage__message">No configured treatments are available.</p>
            ) : (
              <ComparisonChart data={chartData} />
            )}
          </section>
          <section className="model-usage__chart-panel" aria-labelledby="model-usage-pair-title">
            <div className="model-usage__chart-heading"><h2 id="model-usage-pair-title">Pair comparisons</h2></div>
            {pairs.length ? <PairTable pairs={pairs} /> : <p className="model-usage__message">No completed pair comparisons in this period.</p>}
          </section>
        </>
      )}
    </section>
  );
};

export default ModelUsage;
